import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useCallback, type ReactNode } from 'react';

import { applyBillingDevTrialHistory } from '@/components/dev/billing-dev-trial-history';
import {
  billingDevOverrideAtom,
  type BillingDevForm,
} from '@/features/billing-dev/overrides';
import {
  BILLING_DEV_SEAT_OPTIONS,
  BILLING_DEV_TIER_OPTIONS,
  buildBillingDevOverride,
  DEFAULT_BILLING_DEV_FORM,
} from '@/features/billing-dev/presets';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Input } from '@heidi/design-system/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';

/**
 * Dev-tools sidebar section for billing / tier / trial state.
 *
 * Applies a patch over the real `getUserBillingInfo` response (see
 * `src/features/billing-dev/overrides.ts`) so tier, seat, and trial surfaces
 * update live. Trial availability is a user-query history patch, re-applied
 * by `useSyncBillingDevTrialHistory` so a refetch can't wipe it.
 */
export const BillingDevSection = () => {
  const queryClient = useQueryClient();
  const [override, setOverride] = useAtom(billingDevOverrideAtom);
  const form = override?.form ?? null;

  const apply = useCallback(
    (next: BillingDevForm) => {
      setOverride(buildBillingDevOverride(next));
      if (!next.trialing) {
        applyBillingDevTrialHistory(queryClient, next.trialAvailable);
      }
    },
    [queryClient, setOverride]
  );

  const clearOverride = useCallback(() => {
    setOverride(null);
  }, [setOverride]);

  return (
    <section aria-labelledby="dev-tools-billing-heading">
      <TypographyP2SemiBold id="dev-tools-billing-heading" className="mb-2">
        Billing / trial state
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <TypographyCaption className="text-text-tertiary">
          Overrides tier, seat, and trial on the billing-info read. Per-tab; ignored in
          production.
        </TypographyCaption>

        <div className="flex items-center justify-between gap-3">
          <TypographyCaption className="text-text-secondary">
            Active override
          </TypographyCaption>
          {override ? (
            <div className="flex items-center gap-2">
              <Badge size="xs" color="green">
                {override.label}
              </Badge>
              <Button variant="ghost" size="xxs" onClick={clearOverride}>
                Clear
              </Button>
            </div>
          ) : (
            <TypographyCaption className="text-text-tertiary">
              none — real account state
            </TypographyCaption>
          )}
        </div>

        <CheckRow
          id="billing-dev-real-account"
          label="Real account state"
          description="No override — whatever your account actually is."
          checked={form == null}
          onCheckedChange={(checked) => {
            if (checked) {
              clearOverride();
              return;
            }
            apply(form ?? DEFAULT_BILLING_DEV_FORM);
          }}
        />

        {form ? <BillingDevControls form={form} onChange={apply} /> : null}
      </div>
    </section>
  );
};

const BillingDevControls = ({
  form,
  onChange,
}: {
  form: BillingDevForm;
  onChange: (next: BillingDevForm) => void;
}) => {
  const setCountdown = (field: 'trialDaysLeft' | 'trialHoursLeft', raw: string) => {
    const next = Math.max(0, Number.parseInt(raw, 10) || 0);
    onChange({ ...form, [field]: next });
  };
  const tierOptions = form.trialing
    ? BILLING_DEV_TIER_OPTIONS.filter((option) => option.value !== 'free')
    : BILLING_DEV_TIER_OPTIONS;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Tier">
        <Select
          density="compact"
          value={form.tier}
          onValueChange={(tier) =>
            onChange({ ...form, tier: tier as BillingDevForm['tier'] })
          }
        >
          <SelectTrigger className="w-full" aria-label="Tier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[10001]">
            {tierOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Seat type">
        <Select
          density="compact"
          value={form.seatType}
          onValueChange={(seatType) =>
            onChange({ ...form, seatType: seatType as BillingDevForm['seatType'] })
          }
        >
          <SelectTrigger className="w-full" aria-label="Seat type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[10001]">
            {BILLING_DEV_SEAT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <CheckRow
        id="billing-dev-trialing"
        label="Trialing"
        description="Counts down on upgrade CTAs and the usage meter."
        checked={form.trialing}
        onCheckedChange={(trialing) =>
          onChange({
            ...form,
            trialing,
            tier: trialing && form.tier === 'free' ? 'clinician' : form.tier,
          })
        }
      />

      {form.trialing ? (
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Days left">
              <Input
                type="number"
                min={0}
                value={form.trialDaysLeft}
                onChange={(event) => setCountdown('trialDaysLeft', event.target.value)}
                aria-label="Days left"
              />
            </Field>
            <Field label="Hours left">
              <Input
                type="number"
                min={0}
                value={form.trialHoursLeft}
                onChange={(event) => setCountdown('trialHoursLeft', event.target.value)}
                aria-label="Hours left"
              />
            </Field>
          </div>
          <TypographyCaption className="text-text-tertiary">
            Countdown stays in days until the total is under 24 hours.
          </TypographyCaption>
        </div>
      ) : (
        <CheckRow
          id="billing-dev-trial-available"
          label="Trial available"
          description='No trial history. Shows "Try Clinician for free".'
          checked={form.trialAvailable}
          onCheckedChange={(trialAvailable) => onChange({ ...form, trialAvailable })}
        />
      )}
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1">
    <TypographyCaption className="text-text-secondary">{label}</TypographyCaption>
    {children}
  </div>
);

const CheckRow = ({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <label htmlFor={id} className="flex cursor-pointer items-start gap-2.5">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(next) => onCheckedChange(next === true)}
      className="mt-0.5"
      aria-label={label}
    />
    <span className="flex min-w-0 flex-col gap-0.5">
      <TypographyCaptionBold>{label}</TypographyCaptionBold>
      <TypographyCaption className="whitespace-normal text-text-tertiary">
        {description}
      </TypographyCaption>
    </span>
  </label>
);
